import { Button } from '@/components/ui';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './StepperModalFooter.module.scss';

// ============================================
// TYPES
// ============================================

interface StepperModalFooterProps {
  /** Whether the current step is the first (hides Back button) */
  isFirstStep: boolean;
  /** Whether the current step is the last (shows submit label instead of Next) */
  isLastStep: boolean;
  /** Called when Next/Submit is clicked */
  onNext: () => void;
  /** Called when Back is clicked */
  onPrev: () => void;
  /** Called when Cancel is clicked */
  onCancel: () => void;
  /** Disables all buttons and shows spinner on primary */
  isSubmitting?: boolean;
  /** Label for the final step button (default: "Submit") */
  submitLabel?: string;
  /** Label for cancel button (default: "Cancel") */
  cancelLabel?: string;
  /** Disables the Next/Submit button independently of isSubmitting */
  disableNext?: boolean;
}

// ============================================
// COMPONENT
// ============================================

function StepperModalFooter({
  isFirstStep,
  isLastStep,
  onNext,
  onPrev,
  onCancel,
  isSubmitting = false,
  submitLabel,
  cancelLabel,
  disableNext = false,
}: StepperModalFooterProps) {
  const { t } = useTranslation();
  const resolvedSubmitLabel = submitLabel ?? t('K_SUBMIT', 'Submit');
  const resolvedCancelLabel = cancelLabel ?? t('K_CANCEL', 'Cancel');

  return (
    <div className={styles.footer}>
      <Button
        variant="outline"
        size="lg"
        onClick={isFirstStep ? onCancel : onPrev}
        disabled={isSubmitting}
      >
        {isFirstStep ? resolvedCancelLabel : t('K_BACK', 'Back')}
      </Button>
      <Button
        variant="filled"
        size="lg"
        onClick={onNext}
        loading={isSubmitting}
        disabled={isSubmitting || disableNext}
      >
        {isLastStep ? resolvedSubmitLabel : t('K_NEXT', 'Next')}
      </Button>
    </div>
  );
}

export default StepperModalFooter;
